import { memo, useEffect, useState } from "react";
import Select from "react-select";
import { useGetUsersQuery } from "../../../features/ManageUsers/manageUsersSlice";
import toast from "react-hot-toast";
import { useDebounce } from "../../../hooks/useDebounce";
import "./UserSelect.css";

const UserSelect = ({
  label,
  value,
  onChange,
  isDisabled = false,
  placeholder = "Select",
}) => {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState(value?.label || "");
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [options, setOptions] = useState([]);
  const debounceSearch = useDebounce(query);

  const {
    data: usersData,
    error: usersError,
    isFetching,
  } = useGetUsersQuery({
    email: "",
    query: "",
    orderBy: "name",
    orderByType: "Asc",
    name: debounceSearch,
    page,
    pageSize,
  });

  useEffect(() => {
    if (usersData) {
      const users = usersData?.ids.map((id) => usersData?.entities[id]);

      const newOptions = users?.map((user) => ({
        label: user.name,
        value: user.id,
      }));

      setOptions((prevOptions) => {
        const seen = new Set();
        const mergedOptions = [...prevOptions, ...newOptions].filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });
        return mergedOptions;
      });

      setHasMore(users?.length >= pageSize);
    }

    if (usersError) {
      toast.error(usersError?.data?.Message || "Failed to load");
    }
  }, [usersData, usersError, pageSize]);

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isFetching) {
      setPage((prev) => prev + 1);
    }
  };

  const selectedOption =
    options.find((opt) =>
      typeof value === "object" && value !== null
        ? opt.value === value.value
        : opt.value === value
    ) || null;

  const handleSearch = (inputValue) => {
    setQuery(inputValue);
    setPage(1);
    if (inputValue) {
      setOptions([]);
    }
  };

  const handleChange = (selected) => {
    onChange(selected || null);
    if (!selected) {
      handleSearch("");
    }
  };

  return (
    <>
      {label && <label className="custom-label">{label}</label>}
      <Select
        className="paginated-users-selector"
        classNamePrefix="custom-select"
        options={options}
        value={selectedOption}
        isDisabled={isDisabled}
        isClearable={true}
        isLoading={isFetching}
        onChange={handleChange}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        onInputChange={(inputValue, { action }) => {
          if (action === "input-change") {
            handleSearch(inputValue);
          }
        }}
        placeholder={placeholder}
      />
    </>
  );
};

export default memo(UserSelect);
